import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

/**
 * Class wrapping information coming over the network. Indicates whether the information
 * is still being received, and in the case that the information has been received, what
 * the information was.
 *
 * @author Jacob
 */
export class Loadable<T> {
  /**
   * Determines whether currently loading
   */
  isLoading = false;

  /**
   * Determines whether currently in an error state
   */
  error?: string | Error | HttpErrorResponse;
  get errorMessage(): string {
    return this.error instanceof Error || this.error instanceof HttpErrorResponse
      ? this.error.message
      : this.error ?? 'Unexpected Error';
  }

  /**
   * Value  of Loadable<T>, or null if currently loading
   */
  private _value?: T | null;

  /**
   * Gets the value currently contained by this Loadable<T>
   */
  public get value() {
    return this._value;
  }

  /** Loadable is untouched if it is not loading and its value is still in its undefined state */
  get untouched(): boolean {
    return !this.isLoading && !this.error && this._value === undefined;
  }

  /** Loadable is loaded if it is not loading and its value has a defined value */
  get loaded(): boolean {
    return !this.isLoading && this._value !== undefined && this._value !== null;
  }

  /**
   * Creates a new Loadable<T>
   * @param value the initial value for this Loadable<T>
   * @param loading whether this Loadable<T> is loading
   * @param error whether this Loadable<T> is currently in an error state
   */
  constructor(
    value: T | null | undefined = undefined,
    loading = false,
    error: string | HttpErrorResponse | Error | undefined = undefined
  ) {
    this._value = value;
    this.isLoading = loading;
    this.error = error;
  }

  /**
   * @param loading whether loading
   * @returns a copy of this Loadable<T> with the same value and error status
   */
  loadingCopy(loading: boolean) {
    return new Loadable(structuredClone(this.value), loading, this.error);
  }

  /**
   * @param value the potential new value
   * @returns a copy of this Loadable<T> with the same loading status
   * and error status, but a potentially different value
   */
  valueCopy(value?: T) {
    return new Loadable(structuredClone(value), this.isLoading);
  }

  /**
   *
   * @param error the error status
   * @returns a copy of this Loadable<T> with a different
   * error status
   */
  errorCopy(error: string | HttpErrorResponse | Error | undefined) {
    return new Loadable(structuredClone(this.value), false, error);
  }

  /**
   * @returns an identical copy of this Loadable<T>
   * @param options if specified can replace certain or all members
   * of the original object in the new one
   */
  copy(options?: {
    value?: T | null;
    loading?: boolean;
    error?: string | HttpErrorResponse | Error;
  }) {
    return new Loadable(
      options?.value !== undefined ? options.value : structuredClone(this.value),
      options?.loading !== undefined ? options.loading : this.isLoading,
      options?.error !== undefined ? options.error : this.error
    );
  }
}

/** Returns a copy of a type with all attributes nullable and added "error" string attribute */
export type MayError<T> = Partial<T> & { error?: string };

/**
 * RxJS pipe operator executes a callback function on one
 * or many `Loadable` observables if they are `untouched`.
 *
 * Use `pick` argument to choose observables at which indexes should be checked,
 * if not specified all will be checked
 *
 * Callback gets observable's value, but similarly to the `tap` operator does not modify it
 * when passing down a pipe
 */
export function onUntouched<T>(callbackOrNextMap: {
  [index: number]: (value: T) => void;
}): (o: Observable<T>) => Observable<T>;
export function onUntouched<T>(
  callbackOrNextMap: (value: T) => void,
  pick?: number | number[]
): (o: Observable<T>) => Observable<T>;
export function onUntouched<T>(
  callbackOrNextMap: ((value: T) => void) | { [index: number]: (value: T) => void },
  pick?: number | number[]
): (o: Observable<T>) => Observable<T> {
  const check = (value: T): boolean => {
    if (!(value instanceof Loadable)) {
      console.error('[onUntouched] Observable is not `Loadable`. Cannot execute your function');
      return false;
    }

    return value.untouched;
  };

  return (observable: Observable<T>) =>
    new Observable<T>(subscriber => {
      const subscription = observable.subscribe({
        next(value) {
          if (typeof callbackOrNextMap === 'function') {
            // If callbackOrNextMap is a function,
            // run it for the provided value or a list of values
            if (!Array.isArray(value)) {
              if (check(value)) callbackOrNextMap(value);
            } else {
              let pass = 0;
              value.forEach((obs, index) => {
                if (
                  pick === undefined ||
                  pick === index ||
                  (Array.isArray(pick) && pick.includes(index))
                )
                  pass += check(obs) ? 1 : 0;
              });
  
              if (
                (pick === undefined && pass === value.length) ||
                (pick !== undefined && !Array.isArray(pick) && pass === 1) ||
                (pick !== undefined && Array.isArray(pick) && pass === pick.length)
              )
                callbackOrNextMap(value);
            }
          } else {
            // If callbackOrNextMap is an object,
            // use it as a map of callbacks for a specific index in a list of values
            if (!Array.isArray(value))
              throw new Error("Action map specification of onUntouched used against a non-array observable. Check your observable or rewrite onUntouched with a different specification");

            for (const sIndex in callbackOrNextMap) {
              const index = Number(sIndex);
              if (check(value[index])) callbackOrNextMap[index](value);
            }
          }

          subscriber.next(value);
        },
        error(err) {
          subscriber.error(err);
        },
        complete() {
          subscriber.complete();
        },
      });

      return () => subscription.unsubscribe();
    });
}
